// Shopify App Home: the merchant dashboard inside the Shopify admin iframe. The server serves the
// same SPA build at /shopify/app with App Bridge loaded first. In this mode every API request is
// authenticated with a fresh App Bridge ID token instead of the www.perzo.art cookie session, and
// no browser storage is used for authentication.

/** React Router basename in App Home: '/app/styles' renders at '/shopify/app/styles'. */
export const APP_HOME_BASENAME = '/shopify';
export const APP_HOME_PATH = '/shopify/app';

interface AppBridge {
  idToken(): Promise<string>;
  config?: { shop?: string; host?: string };
  /** The admin's contextual Save Bar (<ui-save-bar>): while shown, Shopify admin itself asks before Back and its menu. */
  saveBar?: { show(id: string): Promise<void>; hide(id: string): Promise<void> };
}

declare global {
  interface Window {
    shopify?: AppBridge;
  }
}

export function isAppHomePath(pathname: string): boolean {
  return pathname === APP_HOME_PATH || pathname.startsWith(`${APP_HOME_PATH}/`);
}

// Decided once per document: the mode cannot change without a full page load.
const EMBEDDED = typeof window !== 'undefined' && isAppHomePath(window.location.pathname);

export function isEmbeddedAdmin(): boolean {
  return EMBEDDED;
}

/** PERZO_AFFILIATES: only the first bootstrap carries the referral cookie (the Shopify bearer stays required). */
export function affiliateBootstrapCredentials(): RequestCredentials | undefined {
  return typeof document !== 'undefined' && document.querySelector<HTMLMetaElement>('meta[name="perzo-affiliates"]')?.content === 'on' ? 'include' : undefined;
}

export class AppBridgeUnavailableError extends Error {
  constructor() {
    super('Shopify App Bridge did not load. Reload Perzo from Shopify admin.');
    this.name = 'AppBridgeUnavailableError';
  }
}

/** A fresh ID token for each request; App Bridge caches and renews it internally. */
export async function shopifyIdToken(): Promise<string> {
  const bridge = typeof window !== 'undefined' ? window.shopify : undefined;
  if (!bridge || typeof bridge.idToken !== 'function') throw new AppBridgeUnavailableError();
  return bridge.idToken();
}

/** Leaves the iframe. App Bridge routes window.open(url, '_top') through Shopify admin. */
export function openTopLevel(url: string): void {
  window.open(url, '_top');
}

/** Opens a first-party www.perzo.art page in a new browser tab (outside Shopify admin). */
export function openOutsideAdmin(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Shopify appends a short-lived id_token (and its signature) to the launch URL. Authentication never
 * reads it from there, so it is dropped from the iframe's address and history on boot.
 */
export function launchUrlWithoutToken(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has('id_token')) return null;
  url.searchParams.delete('id_token');
  return url.pathname + url.search + url.hash;
}

export function scrubLaunchToken(): void {
  const next = launchUrlWithoutToken(window.location.href);
  if (next !== null) window.history.replaceState(window.history.state, '', next);
}

/**
 * Theme editor link that adds the Perzo app block (extensions/perzo-personalizer/blocks/personalizer.liquid)
 * to the product template. The app's client ID comes from the meta tag App Home is served with.
 */
export function themeEditorBlockUrl(shop: string | null | undefined, clientId?: string | null): string | null {
  return themeEditorUrl(shop, clientId, (apiKey) => ({ template: 'product', addAppBlockId: `${apiKey}/personalizer`, target: 'mainSection' }));
}

/**
 * Theme editor link that turns on the optional "Perzo cart art" app embed
 * (extensions/perzo-personalizer/blocks/perzo-cart.liquid): the shopper's art as the cart line picture.
 */
export function themeEditorCartEmbedUrl(shop: string | null | undefined, clientId?: string | null): string | null {
  return themeEditorUrl(shop, clientId, (apiKey) => ({ context: 'apps', template: 'product', activateAppId: `${apiKey}/perzo-cart` }));
}

function themeEditorUrl(shop: string | null | undefined, clientId: string | null | undefined, params: (apiKey: string) => Record<string, string>): string | null {
  // Inside Shopify admin from the meta tag; on www.perzo.art from the setup status (the public client id).
  const meta = typeof document !== 'undefined' ? document.querySelector<HTMLMetaElement>('meta[name="shopify-api-key"]')?.content?.trim() : '';
  const apiKey = meta || clientId?.trim() || '';
  if (!apiKey || !shop || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop)) return null;
  return `https://${shop}/admin/themes/current/editor?${new URLSearchParams(params(apiKey))}`;
}

export interface EmbeddedSession {
  shop: string;
  merchant: { id: string; name: string };
  ownerAccount: boolean;
  connectionInstalled: boolean;
  staff: { userId: string; staffUserId: string };
  /** Absent from servers before the plan gate: treated as no gate. */
  plan?: { active: boolean; confirming?: boolean; selectionUrl: string | null };
}

/**
 * App Home pages a store without a usable plan may still open (Shopify: merchants without an active
 * subscription go to plan selection first). Everything else shows the plan screen instead.
 */
const PLAN_FREE_PAGES = ['/app/billing', '/app/settings', '/app/support'];

/** Whether App Home shows `pathname` (router path, e.g. '/app/styles') to this store as it is now. */
export function planGateAllows(plan: EmbeddedSession['plan'] | null | undefined, pathname: string): boolean {
  if (!plan || plan.active) return true;
  const path = pathname.replace(/\/+$/, '');
  return PLAN_FREE_PAGES.some((page) => path === page || path.startsWith(`${page}/`));
}
